/**
 * ScorecardView Component
 *
 * Displays a golf round in traditional scorecard format with
 * colour-coded cells indicating score relative to par.
 */

import { SCORE_COLOURS, SCORE_TYPES } from '../utility/golfConfig';

/**
 * Get background colour for a score result
 */
const getScoreColour = (scoreType) => {
    return SCORE_COLOURS[scoreType] || '#ffffff';
};

/**
 * Get text colour based on background
 */
const getTextColour = (scoreType) => {
    // Light backgrounds need dark text
    if (scoreType === SCORE_TYPES.PAR || scoreType === SCORE_TYPES.EAGLE_OR_BETTER) {
        return '#000000';
    }
    // Dark backgrounds need light text
    if (scoreType === SCORE_TYPES.DOUBLE_BOGEY || scoreType === SCORE_TYPES.TRIPLE_OR_WORSE) {
        return '#ffffff';
    }
    return '#000000';
};

/**
 * Format score relative to par for display
 */
const formatRelativeToPar = (relativeToPar) => {
    if (relativeToPar === 0) return 'E';
    if (relativeToPar > 0) return `+${relativeToPar}`;
    return relativeToPar.toString();
};

/**
 * Individual hole cell component
 */
const HoleCell = ({ hole, isHeader = false }) => {
    if (isHeader) {
        return (
            <td className="px-1 py-1 text-center text-xs font-semibold bg-gray-100 border border-gray-300">
                {hole.number}
            </td>
        );
    }

    const bgColour = getScoreColour(hole.scoreType);
    const textColour = getTextColour(hole.scoreType);

    return (
        <td
            className="px-1 py-1 text-center text-xs font-medium border border-gray-300"
            style={{ backgroundColor: bgColour, color: textColour }}
            title={`Hole ${hole.hole}: ${hole.result} (${hole.score})`}
        >
            {hole.score}
        </td>
    );
};

/**
 * Total cell component
 */
const TotalCell = ({ value, label, isHeader = false }) => {
    if (isHeader) {
        return (
            <td className="px-2 py-1 text-center text-xs font-semibold bg-gray-200 border border-gray-300">
                {label}
            </td>
        );
    }

    return (
        <td className="px-2 py-1 text-center text-sm font-bold bg-gray-50 border border-gray-300">
            {value}
        </td>
    );
};

/**
 * Par row component
 */
const ParRow = ({ holes }) => {
    const frontNinePar = holes.slice(0, 9).reduce((sum, h) => sum + h.par, 0);
    const backNinePar = holes.slice(9, 18).reduce((sum, h) => sum + h.par, 0);
    const totalPar = frontNinePar + backNinePar;

    return (
        <tr>
            <td className="px-2 py-1 text-xs font-semibold bg-gray-100 border border-gray-300">Par</td>
            {holes.slice(0, 9).map((hole, i) => (
                <td key={i} className="px-1 py-1 text-center text-xs bg-gray-50 border border-gray-300">
                    {hole.par}
                </td>
            ))}
            <td className="px-2 py-1 text-center text-xs font-semibold bg-gray-100 border border-gray-300">
                {frontNinePar}
            </td>
            {holes.slice(9, 18).map((hole, i) => (
                <td key={i + 9} className="px-1 py-1 text-center text-xs bg-gray-50 border border-gray-300">
                    {hole.par}
                </td>
            ))}
            <td className="px-2 py-1 text-center text-xs font-semibold bg-gray-100 border border-gray-300">
                {backNinePar}
            </td>
            <td className="px-2 py-1 text-center text-xs font-bold bg-gray-200 border border-gray-300">
                {totalPar}
            </td>
        </tr>
    );
};

/**
 * Single nine-hole scorecard table (used in stacked mobile layout)
 */
const NineHoleTable = ({ holes, label, subtotal, totalScore, showTotal = false }) => {
    const parTotal = holes.reduce((sum, h) => sum + h.par, 0);

    return (
        <table className="w-full border-collapse text-sm">
            <thead>
                <tr>
                    <th className="px-2 py-1 text-left text-xs font-semibold bg-gray-200 border border-gray-300">
                        Hole
                    </th>
                    {holes.map((hole, i) => (
                        <HoleCell key={i} hole={hole} isHeader={true} />
                    ))}
                    <TotalCell label={label} isHeader={true} />
                    {showTotal && <TotalCell label="Tot" isHeader={true} />}
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td className="px-2 py-1 text-xs font-semibold bg-gray-100 border border-gray-300">Par</td>
                    {holes.map((hole, i) => (
                        <td key={i} className="px-1 py-1 text-center text-xs bg-gray-50 border border-gray-300">
                            {hole.par}
                        </td>
                    ))}
                    <td className="px-2 py-1 text-center text-xs font-semibold bg-gray-100 border border-gray-300">
                        {parTotal}
                    </td>
                    {showTotal && (
                        <td className="px-2 py-1 text-center text-xs font-bold bg-gray-200 border border-gray-300">
                            {holes.reduce((s, h) => s + h.par, 0) + (totalScore != null ? 0 : 0)}
                        </td>
                    )}
                </tr>
                <tr>
                    <td className="px-2 py-1 text-xs font-semibold bg-green-100 border border-gray-300">Score</td>
                    {holes.map((hole, i) => (
                        <HoleCell key={i} hole={hole} />
                    ))}
                    <td className="px-2 py-1 text-center text-sm font-bold bg-green-50 border border-gray-300">
                        {subtotal}
                    </td>
                    {showTotal && (
                        <td className="px-2 py-1 text-center text-sm font-bold bg-green-200 border border-gray-300">
                            {totalScore}
                        </td>
                    )}
                </tr>
            </tbody>
        </table>
    );
};

/**
 * Main scorecard component
 */
export const ScorecardView = ({ round, course }) => {
    if (!round || !round.holes || round.holes.length === 0) {
        return null;
    }

    const frontNine = round.holes.slice(0, 9);
    const backNine = round.holes.slice(9, 18);

    return (
        <div>
            {/* Desktop: single row with all 18 holes */}
            <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full border-collapse text-sm">
                    <thead>
                        <tr>
                            <th className="px-2 py-1 text-left text-xs font-semibold bg-gray-200 border border-gray-300">
                                Hole
                            </th>
                            {frontNine.map((hole, i) => (
                                <HoleCell key={i} hole={hole} isHeader={true} />
                            ))}
                            <TotalCell label="Out" isHeader={true} />
                            {backNine.map((hole, i) => (
                                <HoleCell key={i + 9} hole={hole} isHeader={true} />
                            ))}
                            <TotalCell label="In" isHeader={true} />
                            <TotalCell label="Tot" isHeader={true} />
                        </tr>
                    </thead>
                    <tbody>
                        <ParRow holes={round.holes} />
                        <tr>
                            <td className="px-2 py-1 text-xs font-semibold bg-green-100 border border-gray-300">
                                Score
                            </td>
                            {frontNine.map((hole, i) => (
                                <HoleCell key={i} hole={hole} />
                            ))}
                            <td className="px-2 py-1 text-center text-sm font-bold bg-green-50 border border-gray-300">
                                {round.frontNine}
                            </td>
                            {backNine.map((hole, i) => (
                                <HoleCell key={i + 9} hole={hole} />
                            ))}
                            <td className="px-2 py-1 text-center text-sm font-bold bg-green-50 border border-gray-300">
                                {round.backNine}
                            </td>
                            <td className="px-2 py-1 text-center text-sm font-bold bg-green-200 border border-gray-300">
                                {round.totalScore}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Mobile: stacked front nine / back nine */}
            <div className="md:hidden space-y-2">
                <NineHoleTable
                    holes={frontNine}
                    label="Out"
                    subtotal={round.frontNine}
                />
                <NineHoleTable
                    holes={backNine}
                    label="In"
                    subtotal={round.backNine}
                    showTotal={true}
                    totalScore={round.totalScore}
                />
            </div>

            {/* Score summary */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex flex-wrap gap-4 justify-center text-sm">
                    <ScoreSummaryItem
                        label="Total"
                        value={round.totalScore}
                        subValue={formatRelativeToPar(round.relativeToPar)}
                        highlight={true}
                    />
                    <ScoreSummaryItem label="Eagles" value={round.eagles} colour={SCORE_COLOURS[SCORE_TYPES.EAGLE_OR_BETTER]} />
                    <ScoreSummaryItem label="Birdies" value={round.birdies} colour={SCORE_COLOURS[SCORE_TYPES.BIRDIE]} />
                    <ScoreSummaryItem label="Pars" value={round.pars} colour={SCORE_COLOURS[SCORE_TYPES.PAR]} />
                    <ScoreSummaryItem label="Bogeys" value={round.bogeys} colour={SCORE_COLOURS[SCORE_TYPES.BOGEY]} />
                    <ScoreSummaryItem label="Doubles" value={round.doubleBogeys} colour={SCORE_COLOURS[SCORE_TYPES.DOUBLE_BOGEY]} />
                    <ScoreSummaryItem label="Triple+" value={round.tripleOrWorse} colour={SCORE_COLOURS[SCORE_TYPES.TRIPLE_OR_WORSE]} />
                </div>
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
                <LegendItem scoreType={SCORE_TYPES.EAGLE_OR_BETTER} label="Eagle+" />
                <LegendItem scoreType={SCORE_TYPES.BIRDIE} label="Birdie" />
                <LegendItem scoreType={SCORE_TYPES.PAR} label="Par" />
                <LegendItem scoreType={SCORE_TYPES.BOGEY} label="Bogey" />
                <LegendItem scoreType={SCORE_TYPES.DOUBLE_BOGEY} label="Double" />
                <LegendItem scoreType={SCORE_TYPES.TRIPLE_OR_WORSE} label="Triple+" />
            </div>
        </div>
    );
};

/**
 * Score summary item
 */
const ScoreSummaryItem = ({ label, value, subValue, colour, highlight = false }) => {
    return (
        <div className={`text-center px-3 py-2 rounded-lg ${highlight ? 'bg-green-100' : ''}`}>
            <div className="text-xs text-gray-500">{label}</div>
            <div
                className="text-lg font-bold"
                style={colour ? { color: colour } : {}}
            >
                {value}
                {subValue && (
                    <span className="text-sm font-normal text-gray-500 ml-1">
                        ({subValue})
                    </span>
                )}
            </div>
        </div>
    );
};

/**
 * Legend item
 */
const LegendItem = ({ scoreType, label }) => {
    const bgColour = getScoreColour(scoreType);
    const textColour = getTextColour(scoreType);

    return (
        <div className="flex items-center gap-1 text-xs">
            <span
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: bgColour }}
            />
            <span style={{ color: textColour === '#ffffff' ? '#374151' : textColour }}>
                {label}
            </span>
        </div>
    );
};

/**
 * Compact scorecard for simulation results
 */
export const CompactScorecard = ({ round }) => {
    if (!round) return null;

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm">
            <div className="flex justify-between items-center mb-2">
                <span className="font-semibold">Round Score</span>
                <span className="text-xl font-bold text-green-700">
                    {round.totalScore}
                    <span className="text-sm text-gray-500 ml-1">
                        ({formatRelativeToPar(round.relativeToPar)})
                    </span>
                </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div>
                    <div className="text-gray-500">Front 9</div>
                    <div className="font-semibold">{round.frontNine}</div>
                </div>
                <div>
                    <div className="text-gray-500">Back 9</div>
                    <div className="font-semibold">{round.backNine}</div>
                </div>
                <div>
                    <div className="text-gray-500">Expected</div>
                    <div className="font-semibold">{round.expectedScore}</div>
                </div>
            </div>
        </div>
    );
};
